import type { ProgressState } from "../application/progress-state";
import type { ArenaBackgroundSelectionStatus } from "../application/arena-background-rules";
import {
  observeProgressMutation,
  type ProgressMutation,
} from "../application/progress-mutation";
import {
  arenaBackgroundConfigs,
  arenaBackgroundIds,
  type ArenaBackgroundId,
} from "../config/arena-background-config";

export type StoredArenaBackgroundSelectionStatus =
  ArenaBackgroundSelectionStatus | "persistence-failed";

export interface ArenaBackgroundShellStore {
  getState(): ProgressState;
  selectArenaBackground(
    id: string,
    expectedSelectedId: string,
  ): ProgressMutation<StoredArenaBackgroundSelectionStatus>;
}

interface ArenaBackgroundTile {
  readonly button: HTMLButtonElement;
  readonly handler: () => void;
  readonly id: ArenaBackgroundId;
  readonly mark: HTMLElement;
}

const requireElement = (documentScope: Document, id: string): HTMLElement => {
  const element = documentScope.getElementById(id);
  if (element === null) {
    throw new Error(`Missing arena background shell element: ${id}`);
  }
  return element;
};

const cssColor = (color: number): string =>
  `#${color.toString(16).padStart(6, "0")}`;

export class ArenaBackgroundShell {
  private mutationPending = false;
  private readonly tiles: readonly ArenaBackgroundTile[];

  public constructor(
    private readonly store: ArenaBackgroundShellStore,
    private readonly status: HTMLElement,
    tiles: readonly ArenaBackgroundTile[],
  ) {
    this.tiles = tiles;
    for (const tile of tiles) {
      tile.button.addEventListener("click", tile.handler);
    }
    this.render();
  }

  public destroy(): void {
    for (const tile of this.tiles) {
      tile.button.removeEventListener("click", tile.handler);
    }
  }

  public render(): void {
    const selectedId = this.store.getState().selectedArenaBackgroundId;
    for (const tile of this.tiles) {
      const selected = tile.id === selectedId;
      tile.button.disabled = this.mutationPending;
      tile.button.setAttribute("aria-pressed", String(selected));
      tile.mark.textContent = selected ? "✓ Selected" : "";
    }
  }

  public select(id: ArenaBackgroundId): void {
    const expectedSelectedId = this.store.getState().selectedArenaBackgroundId;
    const mutation = this.store.selectArenaBackground(id, expectedSelectedId);
    const name = arenaBackgroundConfigs[id].name;
    const copy: Readonly<Record<StoredArenaBackgroundSelectionStatus, string>> =
      {
        "already-selected": `${name} is already selected.`,
        "invalid-background": "That playground is not available.",
        "persistence-failed": "The playground was not saved.",
        selected: `${name} selected for your next game.`,
        "stale-command": "That selection is out of date. Try again.",
      };
    observeProgressMutation(mutation, {
      complete: (result) => {
        this.mutationPending = false;
        this.status.textContent = copy[result];
        this.render();
      },
      failed: () => {
        this.mutationPending = false;
        this.status.textContent = copy["persistence-failed"];
        this.render();
      },
      pending: () => {
        this.mutationPending = true;
        this.status.textContent = `Saving ${name}…`;
        this.render();
      },
    });
  }
}

export const installArenaBackgroundShell = (
  documentScope: Document,
  store: ArenaBackgroundShellStore,
): ArenaBackgroundShell => {
  const tiles = arenaBackgroundIds.map((id) => {
    const config = arenaBackgroundConfigs[id];
    const button = requireElement(
      documentScope,
      `arena-background-${id}`,
    ) as HTMLButtonElement;
    const mark = requireElement(documentScope, `arena-background-${id}-mark`);
    button.style.setProperty(
      "--arena-background",
      cssColor(config.backgroundColor),
    );
    button.style.setProperty(
      "--arena-boundary",
      cssColor(config.boundaryColor),
    );
    button.style.setProperty("--arena-field", cssColor(config.fieldColor));
    let shell: ArenaBackgroundShell;
    return {
      button,
      handler: () => {
        shell.select(id);
      },
      id,
      mark,
      setShell: (value: ArenaBackgroundShell) => {
        shell = value;
      },
    };
  });
  const shell = new ArenaBackgroundShell(
    store,
    requireElement(documentScope, "arena-background-status"),
    tiles,
  );
  for (const tile of tiles) {
    tile.setShell(shell);
  }
  return shell;
};
